import React, { useState, useEffect, useRef } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import metricsTracker from '../utils/metricsTracker';

const DivisionExam = ({ updateMetrics }) => {
  // Exam state
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsPerPage, setQuestionsPerPage] = useState(20);
  const [examStarted, setExamStarted] = useState(false);
  const [examCompleted, setExamCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [examProgress, setExamProgress] = useState({});
  const [examConfig, setExamConfig] = useState({
    questionCount: 2,
    gradeLevel: '1',
    operation: 'division',
    useTimer: false,
    timeLimit: 30
  });
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  // Virtual keyboard state
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardPosition, setKeyboardPosition] = useState({ x: 400, y: 100 });
  const [activeInput, setActiveInput] = useState(null);
  const mathProblemRefs = useRef({});
  
  // Solution visibility state


  // Calculate total pages for pagination
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);

  // Level difficulty ranges - properly aligned with "Upto X digits"
  const gradeRanges = {
    '1': { minDividend: 1, maxDividend: 99, minDivisor: 1, maxDivisor: 9 },        // Upto 2 digits
    '2': { minDividend: 10, maxDividend: 999, minDivisor: 2, maxDivisor: 99 },     // Upto 3 digits
    '3': { minDividend: 100, maxDividend: 9999, minDivisor: 2, maxDivisor: 99 },   // Upto 4 digits
    '4': { minDividend: 1000, maxDividend: 99999, minDivisor: 10, maxDivisor: 999 }, // Upto 5 digits
    '5': { minDividend: 10000, maxDividend: 999999, minDivisor: 10, maxDivisor: 999 }, // Upto 6 digits
    '6': { minDividend: 100000, maxDividend: 9999999, minDivisor: 100, maxDivisor: 999 }, // Upto 7 digits
    '7': { minDividend: 1000000, maxDividend: 99999999, minDivisor: 100, maxDivisor: 999 }, // Upto 8 digits
    '8': { minDividend: 10000000, maxDividend: 999999999, minDivisor: 100, maxDivisor: 999 } // Upto 9 digits
  };

  // Calculate paper savings
  const calculatePaperSavings = () => {
    const linesPerQuestion = 7; // Reduced from 8 to 7 since we removed separate question header (5 layouts → 4 layouts)
    const validExamQuestionsLength = Number.isFinite(examQuestions.length) ? examQuestions.length : 0;
    const totalLines = validExamQuestionsLength * linesPerQuestion;
    const papersSaved = Math.ceil(totalLines / 25); // 25 lines per A4 sheet
    return { papersSaved, totalLines };
  };

  // Get solution steps for a question - copied exactly from Division.js
  const getSolutionSteps = (question) => {
    const dividendNum = parseInt(question.dividend);
    const divisorNum = parseInt(question.divisor);
    const quotientNum = Math.floor(dividendNum / divisorNum);
    const remainderNum = dividendNum % divisorNum;
    
    return {
      dividend: dividendNum,
      divisor: divisorNum,
      quotient: quotientNum,
      remainder: remainderNum,
      steps: question.divisionSteps
    };
  };

  // Timer functions
  const formatTime = (seconds) => {
    // Handle invalid input
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    const totalSeconds = examConfig.timeLimit * 60;
    setTimeRemaining(totalSeconds);
    setTimerActive(true);
  };

  const stopTimer = () => {
    setTimerActive(false);
  };

  // Calculate division steps in advance
  const calculateDivisionSteps = (dividendNum, divisorNum) => {
    const steps = [];
    let currentWorkingNumber = '';
    let stepNumber = 0;

    const dividendStr = dividendNum.toString();
    let position = 0;
    let currentRemainderPosition = 0; // Track where the remainder is positioned
    let currentRemainderLength = 0; // Track the length of current remainder
    let workingDigitPosition = 0; // Track the position of the digit we're working on
    let previousStepOnesPosition = 0; // Track the ones position of the previous step's (remainder + bring down)

    while (position < dividendStr.length) {
      // Bring down next digit(s)
      if (currentWorkingNumber === '') {
        currentWorkingNumber = dividendStr[position];
        position++;
        currentRemainderPosition = 0; // First step starts at position 0
        currentRemainderLength = 0;
        workingDigitPosition = 0; // First step works on position 0
        previousStepOnesPosition = 0; // First step has no previous step
      } else {
        currentWorkingNumber += dividendStr[position];
        position++;
        // For subsequent steps, the partial product should align with the ones place
        // of the previous step's (remainder + bring down)
        workingDigitPosition = previousStepOnesPosition;
      }

      const workingNum = parseInt(currentWorkingNumber);
      
      // Find how many times divisor goes into working number
      const quotientDigit = Math.floor(workingNum / divisorNum);
      const partialProduct = quotientDigit * divisorNum;
      const remainder = workingNum - partialProduct;

      // Calculate alignment for proper positioning
      let partialProductStartPosition;
      let remainderStartPosition;
      
      // Partial product should align with the ones place of the current working number
      // Since we fill right to left, we need to calculate the start position
      const partialProductLength = partialProduct.toString().length;
      // The ones place is the rightmost digit of the current working number
      const currentWorkingOnesPosition = position - 1; // Position of the rightmost digit of current working number
      partialProductStartPosition = currentWorkingOnesPosition - (partialProductLength - 1); // Start position for right-to-left filling
      
      // Calculate the ones position of this step's (remainder + bring down) for the next step
      const remainderLength = remainder.toString().length;
      const bringDownLength = position < dividendStr.length ? 1 : 0; // Length of bring down digit
      const currentStepTotalLength = remainderLength + bringDownLength;
      // The ones position is the rightmost position of the current step's (remainder + bring down)
      // This should be based on the position of the current working number in the original dividend
      previousStepOnesPosition = position - 1; // The position of the last digit brought down
      
      // Remainder starts at the rightmost position of the partial product
      remainderStartPosition = partialProductStartPosition + partialProduct.toString().length - remainder.toString().length;

      // For the final step, align remainder with the ones place of the partial product
      if (position >= dividendStr.length) {
        // This is the final step - align remainder with the ones place
        const partialProductStr = partialProduct.toString();
        remainderStartPosition = partialProductStartPosition + partialProductStr.length - remainder.toString().length;
      }

      steps.push({
        stepNumber: stepNumber + 1,
        workingNumber: workingNum,
        quotientDigit: quotientDigit.toString(),
        partialProduct: partialProduct.toString(),
        remainder: remainder.toString(),
        bringDown: position < dividendStr.length ? dividendStr[position] : null,
        position: position - 1,
        // Calculate alignment for proper positioning
        remainderStartPosition: remainderStartPosition,
        partialProductStartPosition: partialProductStartPosition,
        bringDownPosition: position, // Where bring-down should be positioned
        isFinalStep: position >= dividendStr.length, // Flag to identify final step
        workingNumberLength: currentWorkingNumber.length,
        workingNumberPosition: currentRemainderPosition,
        workingDigitPosition: workingDigitPosition // Track the actual digit position
      });

      // Update for next step - the new working number starts at the position of the remainder
      currentWorkingNumber = remainder.toString();
      currentRemainderPosition = remainderStartPosition; // Next step starts where this remainder is
      currentRemainderLength = remainder.toString().length;
      stepNumber++;
    }

    return {
      steps: steps,
      finalQuotient: parseInt(steps.map(s => s.quotientDigit).join('')),
      finalRemainder: parseInt(currentWorkingNumber)
    };
  };

  // Helper functions for positioning calculations
  const calculateVerticalLineHeight = (divisionSteps) => {
    if (divisionSteps.length === 0) return 80;
    // Base height for dividend + each step (partial product + remainder + bring down)
    const baseHeight = 80; // dividend area
    const stepHeight = divisionSteps.length * 120; // reduced from 160px to 120px per step to prevent over-extension
    return Math.max(80, baseHeight + stepHeight);
  };

  const calculateQuotientMargin = (divisionSteps, divisor) => {
    // Quotient should be right-shifted 1 space (42px) as it starts above the divisor
    // For larger divisors, adjust the margin to maintain proper alignment
    const baseMargin = 42; // 1 space right shift
    const divisorLength = divisor.toString().length;
    
    // If divisor is larger than 1 digit, adjust margin to prevent left shifting
    if (divisorLength > 1) {
      // Adjust margin based on divisor length to maintain alignment
      return baseMargin + (divisorLength - 1) * 10; // Add 10px per additional digit
    }
    
    return baseMargin;
  };

  const calculateHorizontalLineWidth = (startPosition, contentLength, divisionSteps, stepIndex = null) => {
    // Start from the vertical line (which is at position 0) and extend to cover the content
    const startFromVerticalLine = 0;
    
    if (stepIndex !== null && stepIndex < divisionSteps.length) {
      // For subtraction lines, calculate width to extend to the bring-down value
      const step = divisionSteps[stepIndex];
      if (step.bringDown) {
        // Calculate the total width needed to reach the bring-down position
        // Add extra space for better user experience and differentiation
        const bringDownEndPosition = step.remainderStartPosition + step.remainder.toString().length + 1; // +1 for bring-down
        const width = Math.max(60, (bringDownEndPosition + 2) * 42); // +2 for extra space and better differentiation
        return { width: `${width}px`, marginLeft: `${startFromVerticalLine}px` };
      } else {
        // For final step without bring-down, extend to cover remainder
        const remainderEndPosition = step.remainderStartPosition + step.remainder.toString().length;
        const width = Math.max(60, (remainderEndPosition + 2) * 42); // +2 for extra space and better differentiation
        return { width: `${width}px`, marginLeft: `${startFromVerticalLine}px` };
      }
    } else {
      // For the horizontal line above dividend, use a more reasonable calculation
      const width = Math.max(60, Math.min((contentLength + 2) * 42, 800)); // +2 for extra space and better differentiation, cap at 800px
      return { width: `${width}px`, marginLeft: `${startFromVerticalLine}px` };
    }
  };

  // Generate exam questions for division
  const generateExam = () => {
    const questions = [];
    const range = gradeRanges[examConfig.gradeLevel];
    
    for (let i = 0; i < examConfig.questionCount; i++) {
      const dividend = Math.floor(Math.random() * (range.maxDividend - range.minDividend + 1)) + range.minDividend;
      const divisor = Math.floor(Math.random() * (range.maxDivisor - range.minDivisor + 1)) + range.minDivisor;
      
      // Ensure divisor is not 0 and dividend is divisible
      if (divisor === 0) continue;
      
      const divisionResult = calculateDivisionSteps(dividend, divisor);
      const correctAnswer = divisionResult.finalQuotient;
      const remainder = divisionResult.finalRemainder;
      
      questions.push({
        id: i + 1,
        dividend: dividend.toString(),
        divisor: divisor.toString(),
        correctAnswer: correctAnswer.toString(),
        remainder: remainder.toString(),
        divisionSteps: divisionResult.steps,
        userQuotientDigits: [],
        userPartialProducts: [],
        userRemainders: [],
        userBringDowns: [],
        isCorrect: null,
        explanation: generateExplanation(dividend, divisor, correctAnswer, remainder)
      });
    }
    
    setExamQuestions(questions);
    setTotalQuestions(questions.length);
    setCurrentPage(1);
    setExamStarted(true);
    
    // Track exam started
    metricsTracker.trackExamStarted('division', examConfig.gradeLevel, examConfig.questionCount);
    
    // Start timer if enabled
    if (examConfig.useTimer) {
      startTimer();
    }
  };

  // Generate explanation for division
  const generateExplanation = (dividend, divisor, quotient, remainder) => {
    if (remainder === 0) {
      return `${dividend} ÷ ${divisor} = ${quotient} (exact division)`;
    } else {
      return `${dividend} ÷ ${divisor} = ${quotient} remainder ${remainder}`;
    }
  };



  // Handle quotient digit input
  const handleQuotientDigitChange = (questionId, stepIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newQuestions = [...examQuestions];
      const questionIndex = newQuestions.findIndex(q => q.id === questionId);
      
      if (questionIndex !== -1) {
        if (!newQuestions[questionIndex].userQuotientDigits[stepIndex]) {
          newQuestions[questionIndex].userQuotientDigits[stepIndex] = '';
        }
        newQuestions[questionIndex].userQuotientDigits[stepIndex] = value;
        setExamQuestions(newQuestions);
      }
    }
  };

  // Handle partial product digit input
  const handlePartialProductDigitChange = (questionId, stepIndex, digitIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newQuestions = [...examQuestions];
      const questionIndex = newQuestions.findIndex(q => q.id === questionId);
      
      if (questionIndex !== -1) {
        if (!newQuestions[questionIndex].userPartialProducts[stepIndex]) {
          newQuestions[questionIndex].userPartialProducts[stepIndex] = [];
        }
        if (!newQuestions[questionIndex].userPartialProducts[stepIndex][digitIndex]) {
          newQuestions[questionIndex].userPartialProducts[stepIndex][digitIndex] = '';
        }
        newQuestions[questionIndex].userPartialProducts[stepIndex][digitIndex] = value;
        setExamQuestions(newQuestions);
      }
    }
  };

  // Handle remainder digit input
  const handleRemainderDigitChange = (questionId, stepIndex, digitIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newQuestions = [...examQuestions];
      const questionIndex = newQuestions.findIndex(q => q.id === questionId);
      
      if (questionIndex !== -1) {
        if (!newQuestions[questionIndex].userRemainders[stepIndex]) {
          newQuestions[questionIndex].userRemainders[stepIndex] = [];
        }
        if (!newQuestions[questionIndex].userRemainders[stepIndex][digitIndex]) {
          newQuestions[questionIndex].userRemainders[stepIndex][digitIndex] = '';
        }
        newQuestions[questionIndex].userRemainders[stepIndex][digitIndex] = value;
        setExamQuestions(newQuestions);
      }
    }
  };

  // Handle bring down digit input
  const handleBringDownDigitChange = (questionId, stepIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newQuestions = [...examQuestions];
      const questionIndex = newQuestions.findIndex(q => q.id === questionId);
      
      if (questionIndex !== -1) {
        if (!newQuestions[questionIndex].userBringDowns[stepIndex]) {
          newQuestions[questionIndex].userBringDowns[stepIndex] = '';
        }
        newQuestions[questionIndex].userBringDowns[stepIndex] = value;
        setExamQuestions(newQuestions);
      }
    }
  };

  // Submit exam and calculate results
  const submitExam = () => {
    const updatedQuestions = examQuestions.map(question => {
      let isCorrect = true;
      
      // Check if all quotient digits are correct
      for (let i = 0; i < question.divisionSteps.length; i++) {
        const userQuotient = question.userQuotientDigits[i] || '';
        const correctQuotient = question.divisionSteps[i].quotientDigit;
        
        if (userQuotient !== correctQuotient) {
          isCorrect = false;
          break;
        }
      }
      
      return { ...question, isCorrect };
    });
    
    setExamQuestions(updatedQuestions);
    setExamCompleted(true);
    setShowResults(true);
    stopTimer();
    
    // Calculate and update metrics
    const correctCount = updatedQuestions.filter(q => q.isCorrect).length;
    const totalCount = updatedQuestions.length;
    const score = Math.round((correctCount / totalCount) * 100);
    
    // Track exam submitted
    metricsTracker.trackExamSubmitted('division', examConfig.gradeLevel, score, totalCount, correctCount);
    
    if (updateMetrics) {
      const { papersSaved } = calculatePaperSavings();
      updateMetrics(papersSaved);
    }
  };

  // Save exam to localStorage
  const saveExamToStorage = (questions) => {
    const examData = {
      timestamp: new Date().toISOString(),
      questions: questions,
      config: examConfig
    };
    localStorage.setItem('divisionExamData', JSON.stringify(examData));
  };

  // Load exam from localStorage
  const loadExamFromStorage = () => {
    const savedData = localStorage.getItem('divisionExamData');
    if (savedData) {
      try {
        const examData = JSON.parse(savedData);
        setExamQuestions(examData.questions);
        setTotalQuestions(examData.questions.length);
        setExamConfig(examData.config);
        return true;
      } catch (error) {
        console.error('Error loading exam data:', error);
      }
    }
    return false;
  };

  // Print exam with solutions
  const printExam = () => {
    // Track print workbook
    metricsTracker.trackPrintWorkbook('division', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>BellMaths Exam - Questions and Answers</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .summary {
              margin: 30px 0;
              padding: 20px;
              background-color: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            .summary h3 {
              margin: 0 0 15px 0;
              color: #667eea;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin-bottom: 20px;
            }
            .summary-item {
              padding: 10px;
              background-color: white;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            .summary-label {
              font-weight: bold;
              color: #667eea;
            }
            .summary-value {
              margin-left: 10px;
              font-weight: bold;
            }
            .question { 
              margin-bottom: 40px; 
              page-break-inside: avoid; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .math-problem {
              margin: 20px 0;
            }
            .division-structure {
              font-family: monospace;
              font-size: 18px;
              line-height: 1.8;
            }
            .quotient-row {
              margin-bottom: 20px;
              text-align: right;
              display: flex;
              justify-content: flex-end;
              gap: 2px;
            }
            .quotient-input {
              width: 40px;
              height: 40px;
              text-align: center;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #ffeaea;
              font-size: 16px;
              font-weight: bold;
              color: #e74c3c;
            }
            .main-division-structure {
              display: flex;
              align-items: flex-start;
              gap: 10px;
            }
            .divisor-container {
              margin-top: 10px;
            }
            .divisor-input {
              width: 40px;
              height: 40px;
              text-align: center;
              border: none;
              border-radius: 0;
              font-size: 16px;
              font-weight: bold;
              color: #333;
              background-color: #f8f9fa;
              margin-bottom: 5px;
            }
            .vertical-line {
              width: 2px;
              background-color: #333;
              margin-top: 10px;
              border-left: 2px solid #333;
              min-height: 100px;
            }
            .dividend-working-area {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }
            .horizontal-line-above-dividend {
              height: 2px;
              background-color: #333;
              margin-bottom: 10px;
              border-bottom: 2px solid #333;
              min-width: 100px;
            }
            .dividend-digits {
              display: flex;
              align-items: center;
              gap: 2px;
              margin-bottom: 15px;
            }
            .dividend-digit {
              width: 40px;
              height: 40px;
              text-align: center;
              border: none;
              border-radius: 4px;
              font-size: 16px;
              font-weight: bold;
              color: #333;
              background-color: #f8f9fa;
            }
            .division-step {
              margin-bottom: 10px;
            }
            .partial-product-row {
              display: flex;
              align-items: center;
              gap: 2px;
              margin-bottom: 5px;
            }
            .partial-product-digit {
              width: 40px;
              height: 40px;
              text-align: center;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f5e8;
              font-size: 16px;
              font-weight: bold;
              color: #27ae60;
            }
            .subtraction-line {
              border-bottom: 2px solid #333;
              height: 2px;
              margin-bottom: 10px;
            }
            .remainder-bringdown-row {
              display: flex;
              align-items: center;
              gap: 2px;
              margin-bottom: 5px;
            }
            .remainder-container {
              display: flex;
              align-items: center;
              gap: 2px;
            }
            .remainder-digit {
              width: 40px;
              height: 40px;
              text-align: center;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #fff3e0;
              font-size: 16px;
              font-weight: bold;
              color: #f39c12;
            }
            .bring-down-container {
              display: flex;
              align-items: center;
              gap: 2px;
            }
            .bring-down-digit {
              width: 40px;
              height: 40px;
              text-align: center;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
              font-size: 16px;
              font-weight: bold;
              color: #333;
              margin-right: 5px;
            }
            .bring-down-arrow {
              font-size: 16px;
              font-weight: bold;
              color: #333;
            }
            .solution {
              margin-top: 30px;
              padding: 20px;
              background-color: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            .solution h4 {
              color: #2c3e50;
              margin-bottom: 15px;
            }
            .solution-steps {
              font-size: 14px;
              line-height: 1.8;
            }
            .working-steps-table {
              margin-top: 15px;
            }
            .working-steps-table h5 {
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .working-steps-table table {
              width: 100%;
              border-collapse: collapse;
              background-color: white;
              border-radius: 6px;
              border: 1px solid #dee2e6;
            }
            .working-steps-table th {
              padding: 12px;
              text-align: left;
              border-bottom: 2px solid #dee2e6;
              color: #2c3e50;
              font-weight: bold;
              background-color: #f8f9fa;
            }
            .working-steps-table td {
              padding: 12px;
              border-bottom: 1px solid #dee2e6;
            }
            .quotient-digit {
              color: #e74c3c;
              font-weight: bold;
            }
            .partial-product {
              color: #27ae60;
              font-weight: bold;
            }
            .remainder {
              color: #f39c12;
              font-weight: bold;
            }
            .bring-down {
              color: #333;
            }
            .footer {
              margin-top: 40px;
              padding: 20px;
              text-align: center;
              border-top: 2px solid #e9ecef;
            }
            .page-break {
              page-break-before: always;
            }
            @media print {
              body { background-color: white; }
              .question { box-shadow: none; border: 1px solid #ddd; }
              .header { box-shadow: none; border: 1px solid #ddd; }
              .summary { box-shadow: none; border: 1px solid #ddd; }
              
              /* Ensure colors are preserved in print */
              .quotient-input { 
                background-color: #ffeaea !important; 
                color: #e74c3c !important;
                border: 2px solid #ddd !important;
              }
              .partial-product-digit { 
                background-color: #e8f5e8 !important; 
                color: #27ae60 !important;
                border: 2px solid #ddd !important;
              }
              .remainder-digit { 
                background-color: #fff3e0 !important; 
                color: #f39c12 !important;
                border: 2px solid #ddd !important;
              }
              .bring-down-digit { 
                background-color: #e8f4fd !important; 
                color: #333 !important;
                border: 2px solid #ddd !important;
              }
              .divisor-input { 
                background-color: #f8f9fa !important; 
                color: #333 !important;
              }
              .dividend-digit { 
                background-color: #f8f9fa !important; 
                color: #333 !important;
              }
              
              /* Ensure lines are visible in print */
              .vertical-line { 
                border-left: 2px solid #333 !important; 
                background-color: #333 !important;
              }
              .horizontal-line-above-dividend { 
                border-bottom: 2px solid #333 !important; 
                background-color: #333 !important;
              }
              .subtraction-line { 
                border-bottom: 2px solid #333 !important; 
                background-color: #333 !important;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  // Generate questions only content for printing
  const generateQuestionsOnlyContent = () => {
    let content = '';
    
    content += '<div class="header">';
    content += '<h1>BellMaths - Division Exam</h1>';
    content += '<p>Grade Level: ' + examConfig.gradeLevel + ' | Total Questions: ' + examQuestions.length + '</p>';
    content += '<p>Date: ' + new Date().toLocaleDateString() + '</p>';
    content += '<p>Student Name: _________________</p>';
    content += '<p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>';
    content += '</div>';

    examQuestions.forEach((question, index) => {
      if (index > 0 && index % 3 === 0) {
        content += '<div class="page-break"></div>';
      }
      
      const { dividend, divisor, divisionSteps } = question;
      const dividendDigits = dividend.split('');
      
      content += '<div class="question">';
      content += '<div class="question-header">';
      content += '<h3>Question ' + (index + 1) + '</h3>';
      content += '</div>';
      
      content += '<div class="math-problem">';
      content += '<div class="division-structure">';
      
      // Layout 2: Main Division Structure (Divisor + Vertical Line + Dividend)
      content += '<div class="main-division-structure">';
      
      // Divisor on the left
      content += '<div class="divisor-container">';
      content += '<input type="text" class="divisor-input" value="' + divisor + '" readonly />';
      content += '</div>';
      
      // Vertical line
      content += '<div class="vertical-line" style="height: ' + calculateVerticalLineHeight(divisionSteps) + 'px"></div>';
      
      // Dividend and working area
      content += '<div class="dividend-working-area">';
      
      // Layout 1: Quotient Row - Above horizontal line with exact positioning
      content += '<div class="quotient-row" style="margin-left: ' + calculateQuotientMargin(divisionSteps, divisor) + 'px">';
      divisionSteps.forEach((step, stepIndex) => {
        content += '<input type="text" class="quotient-input" maxlength="1" style="background-color: #ffeaea; color: #e74c3c; border: 2px solid #ddd;" />';
      });
      content += '</div>';
      
      // Horizontal line below quotient with exact positioning
      const horizontalLineStyle = calculateHorizontalLineWidth(0, dividendDigits.length, divisionSteps);
      content += '<div class="horizontal-line-above-dividend" style="width: ' + horizontalLineStyle.width + '; margin-left: ' + horizontalLineStyle.marginLeft + '"></div>';
      
      // Dividend - Individual digit fields
      content += '<div class="dividend-digits">';
      dividendDigits.forEach(digit => {
        content += '<input type="text" class="dividend-digit" value="' + digit + '" readonly />';
      });
      content += '</div>';
      
      // Layout 3: Division Steps with exact positioning
      divisionSteps.forEach((step, stepIndex) => {
        content += '<div class="division-step">';
        
        // Partial Product with exact positioning
        content += '<div class="partial-product-row" style="margin-left: ' + (step.partialProductStartPosition * 42) + 'px">';
        step.partialProduct.toString().split('').forEach(digit => {
          content += '<input type="text" class="partial-product-digit" maxlength="1" style="background-color: #e8f5e8; color: #27ae60; border: 2px solid #ddd;" />';
        });
        content += '</div>';
        
        // Subtraction Line with exact positioning
        const subtractionLineStyle = calculateHorizontalLineWidth(0, step.partialProduct.toString().length, divisionSteps, stepIndex);
        content += '<div class="subtraction-line" style="width: ' + subtractionLineStyle.width + '; margin-left: ' + subtractionLineStyle.marginLeft + '"></div>';
        
        // Remainder and Bring Down on same row with exact positioning
        content += '<div class="remainder-bringdown-row">';
        
        // Remainder with exact positioning
        content += '<div class="remainder-container" style="margin-left: ' + (step.remainderStartPosition * 42) + 'px">';
        step.remainder.toString().split('').forEach(digit => {
          content += '<input type="text" class="remainder-digit" maxlength="1" style="background-color: #fff3e0; color: #f39c12; border: 2px solid #ddd;" />';
        });
        content += '</div>';
        
        // Bring Down (if exists) with exact positioning
        if (step.bringDown) {
          content += '<div class="bring-down-container">';
          content += '<input type="text" class="bring-down-digit" maxlength="1" style="background-color: #e8f4fd; color: #333; border: 2px solid #ddd;" />';
          content += '<span class="bring-down-arrow">↓</span>';
          content += '</div>';
        }
        
        content += '</div>'; // end remainder-bringdown-row
        content += '</div>'; // end division-step
      });
      
      content += '</div>'; // end dividend-working-area
      content += '</div>'; // end main-division-structure
      content += '</div>'; // end division-structure
      content += '</div>'; // end math-problem
      content += '</div>'; // end question
    });

    return content;
  };

  // Generate print content with answers
  const generatePrintContent = () => {
    const results = calculateResults();
    
    let content = '';
    
    content += '<div class="header">';
    content += '<h1>BellMaths - Division Exam</h1>';
    content += '<p>Grade Level: ' + examConfig.gradeLevel + ' | Total Questions: ' + examQuestions.length + '</p>';
    content += '<p>Date: ' + new Date().toLocaleDateString() + '</p>';
    content += '<p>Student Name: _________________</p>';
    content += '<p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>';
    content += '</div>';
    
    content += '<div class="summary">';
    content += '<h3>Exam Results Summary</h3>';
    content += '<div class="summary-grid">';
    content += '<div class="summary-item">';
    content += '<span class="summary-label">Correct Answers:</span>';
    content += '<span class="summary-value">' + results.correctCount + '/' + results.totalCount + '</span>';
    content += '</div>';
    content += '<div class="summary-item">';
    content += '<span class="summary-label">Percentage:</span>';
    content += '<span class="summary-value">' + results.score + '%</span>';
    content += '</div>';
    content += '</div>';
    content += '</div>';

    examQuestions.forEach((question, index) => {
      if (index > 0 && index % 3 === 0) {
        content += '<div class="page-break"></div>';
      }
      
      const { dividend, divisor, divisionSteps } = question;
      const dividendDigits = dividend.split('');
      const solutionSteps = getSolutionSteps(question);
      
      content += '<div class="question">';
      content += '<div class="question-header">';
      content += '<h3>Question ' + (index + 1) + '</h3>';
      content += '</div>';
      
      content += '<div class="math-problem">';
      content += '<div class="division-structure">';
      
      // Layout 2: Main Division Structure (Divisor + Vertical Line + Dividend)
      content += '<div class="main-division-structure">';
      
      // Divisor on the left
      content += '<div class="divisor-container">';
      content += '<input type="text" class="divisor-input" value="' + divisor + '" readonly />';
      content += '</div>';
      
      // Vertical line
      content += '<div class="vertical-line" style="height: ' + calculateVerticalLineHeight(divisionSteps) + 'px"></div>';
      
      // Dividend and working area
      content += '<div class="dividend-working-area">';
      
      // Layout 1: Quotient Row - Above horizontal line (with answers) with exact positioning
      content += '<div class="quotient-row" style="margin-left: ' + calculateQuotientMargin(divisionSteps, divisor) + 'px">';
      divisionSteps.forEach((step, stepIndex) => {
        const quotientDigit = solutionSteps.quotient.toString()[stepIndex] || '';
        content += '<input type="text" class="quotient-input" value="' + quotientDigit + '" readonly style="background-color: #ffeaea; color: #e74c3c; border: 2px solid #ddd;" />';
      });
      content += '</div>';
      
      // Horizontal line below quotient with exact positioning
      const horizontalLineStyle = calculateHorizontalLineWidth(0, dividendDigits.length, divisionSteps);
      content += '<div class="horizontal-line-above-dividend" style="width: ' + horizontalLineStyle.width + '; margin-left: ' + horizontalLineStyle.marginLeft + '"></div>';
      
      // Dividend - Individual digit fields
      content += '<div class="dividend-digits">';
      dividendDigits.forEach(digit => {
        content += '<input type="text" class="dividend-digit" value="' + digit + '" readonly />';
      });
      content += '</div>';
      
      // Layout 3: Division Steps (with answers) with exact positioning
      divisionSteps.forEach((step, stepIndex) => {
        content += '<div class="division-step">';
        
        // Partial Product (with answers) with exact positioning
        content += '<div class="partial-product-row" style="margin-left: ' + (step.partialProductStartPosition * 42) + 'px">';
        step.partialProduct.toString().split('').forEach(digit => {
          content += '<input type="text" class="partial-product-digit" value="' + digit + '" readonly style="background-color: #e8f5e8; color: #27ae60; border: 2px solid #ddd;" />';
        });
        content += '</div>';
        
        // Subtraction Line with exact positioning
        const subtractionLineStyle = calculateHorizontalLineWidth(0, step.partialProduct.toString().length, divisionSteps, stepIndex);
        content += '<div class="subtraction-line" style="width: ' + subtractionLineStyle.width + '; margin-left: ' + subtractionLineStyle.marginLeft + '"></div>';
        
        // Remainder and Bring Down on same row (with answers) with exact positioning
        content += '<div class="remainder-bringdown-row">';
        
        // Remainder (with answers) with exact positioning
        content += '<div class="remainder-container" style="margin-left: ' + (step.remainderStartPosition * 42) + 'px">';
        step.remainder.toString().split('').forEach(digit => {
          content += '<input type="text" class="remainder-digit" value="' + digit + '" readonly style="background-color: #fff3e0; color: #f39c12; border: 2px solid #ddd;" />';
        });
        content += '</div>';
        
        // Bring Down (if exists, with answers) with exact positioning
        if (step.bringDown) {
          content += '<div class="bring-down-container">';
          content += '<input type="text" class="bring-down-digit" value="' + step.bringDown + '" readonly style="background-color: #e8f4fd; color: #333; border: 2px solid #ddd;" />';
          content += '<span class="bring-down-arrow">↓</span>';
          content += '</div>';
        }
        
        content += '</div>'; // end remainder-bringdown-row
        content += '</div>'; // end division-step
      });
      
      content += '</div>'; // end dividend-working-area
      content += '</div>'; // end main-division-structure
      content += '</div>'; // end division-structure
      content += '</div>'; // end math-problem
      
      // Solution Steps
      content += '<div class="solution">';
      content += '<h4>Step-by-Step Solution</h4>';
      content += '<div class="solution-steps">';
      content += '<p><strong>Problem:</strong> ' + solutionSteps.dividend + ' ÷ ' + solutionSteps.divisor + '</p>';
      content += '<p><strong>Answer:</strong> ' + solutionSteps.quotient + ' R ' + solutionSteps.remainder + '</p>';
      
      content += '<div class="working-steps-table">';
      content += '<h5>Working Steps:</h5>';
      content += '<table>';
      content += '<thead>';
      content += '<tr>';
      content += '<th>Step</th>';
      content += '<th>Quotient Digit</th>';
      content += '<th>Partial Product</th>';
      content += '<th>Remainder</th>';
      content += '<th>Bring Down</th>';
      content += '</tr>';
      content += '</thead>';
      content += '<tbody>';
      
      solutionSteps.steps.forEach((step, stepIndex) => {
        content += '<tr>';
        content += '<td>' + (stepIndex + 1) + '</td>';
        content += '<td class="quotient-digit">' + step.quotientDigit + '</td>';
        content += '<td class="partial-product">' + step.partialProduct + ' (' + step.quotientDigit + ' × ' + solutionSteps.divisor + ')</td>';
        content += '<td class="remainder">' + step.remainder + ' (' + step.workingNumber + ' - ' + step.partialProduct + ')</td>';
        content += '<td class="bring-down">' + (step.bringDown ? (step.bringDown + ' → (' + step.remainder + ' + ' + step.bringDown + ')') : '-') + '</td>';
        content += '</tr>';
      });
      
      content += '</tbody>';
      content += '</table>';
      content += '</div>';
      content += '</div>';
      content += '</div>';
      
      content += '</div>'; // end question
    });
    
    content += '<div class="footer">';
    content += '<p>Name: _________________ Date: _________________</p>';
    content += '<p>Score: _____ / ' + examQuestions.length + '</p>';
    content += '</div>';
    
    return content;
  };

  // Print questions only
  const printQuestionsOnly = () => {
    // Track print questions
    metricsTracker.trackPrintQuestions('division', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    const printContent = generateQuestionsOnlyContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>BellMaths Exam - Questions Only</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .question { 
              margin-bottom: 40px; 
              page-break-inside: avoid; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .math-problem {
              margin: 20px 0;
            }
            .division-structure {
              font-family: monospace;
              font-size: 18px;
              line-height: 1.8;
            }
            .quotient-row {
              margin-bottom: 20px;
              text-align: right;
              display: flex;
              justify-content: flex-end;
              gap: 2px;
            }
            .quotient-input {
              width: 40px;
              height: 40px;
              text-align: center;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #ffeaea;
              font-size: 16px;
              font-weight: bold;
              color: #e74c3c;
            }
            .main-division-structure {
              display: flex;
              align-items: flex-start;
              gap: 10px;
            }
            .divisor-container {
              margin-top: 10px;
            }
            .divisor-input {
              width: 40px;
              height: 40px;
              text-align: center;
              border: none;
              border-radius: 0;
              font-size: 16px;
              font-weight: bold;
              color: #333;
              background-color: #f8f9fa;
              margin-bottom: 5px;
            }
            .vertical-line {
              width: 2px;
              height: 200px;
              background-color: #333;
              margin-top: 10px;
            }
            .dividend-working-area {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }
            .horizontal-line-above-dividend {
              width: 200px;
              height: 2px;
              background-color: #333;
              margin-bottom: 10px;
            }
            .dividend-digits {
              display: flex;
              align-items: center;
              gap: 2px;
              margin-bottom: 15px;
            }
            .dividend-digit {
              width: 40px;
              height: 40px;
              text-align: center;
              border: none;
              border-radius: 4px;
              font-size: 16px;
              font-weight: bold;
              color: #333;
              background-color: #f8f9fa;
            }
            .division-step {
              margin-bottom: 10px;
            }
            .partial-product-row {
              display: flex;
              align-items: center;
              gap: 2px;
              margin-bottom: 5px;
            }
            .partial-product-digit {
              width: 40px;
              height: 40px;
              text-align: center;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f5e8;
              font-size: 16px;
              font-weight: bold;
              color: #27ae60;
            }
            .subtraction-line {
              border-bottom: 2px solid #333;
              height: 2px;
              margin-bottom: 10px;
              width: 200px;
            }
            .remainder-bringdown-row {
              display: flex;
              align-items: center;
              gap: 2px;
              margin-bottom: 5px;
            }
            .remainder-digit {
              width: 40px;
              height: 40px;
              text-align: center;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #fff3e0;
              font-size: 16px;
              font-weight: bold;
              color: #f39c12;
            }
            .bring-down-digit {
              width: 40px;
              height: 40px;
              text-align: center;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
              font-size: 16px;
              font-weight: bold;
              color: #333;
              margin-right: 5px;
            }
            .bring-down-arrow {
              font-size: 16px;
              font-weight: bold;
              color: #333;
            }
            .page-break {
              page-break-before: always;
            }
            @media print {
              body { background-color: white; }
              .question { box-shadow: none; border: 1px solid #ddd; }
              .header { box-shadow: none; border: 1px solid #ddd; }
              
              /* Ensure colors are preserved in print */
              .quotient-input { 
                background-color: #ffeaea !important; 
                color: #e74c3c !important;
                border: 2px solid #ddd !important;
              }
              .partial-product-digit { 
                background-color: #e8f5e8 !important; 
                color: #27ae60 !important;
                border: 2px solid #ddd !important;
              }
              .remainder-digit { 
                background-color: #fff3e0 !important; 
                color: #f39c12 !important;
                border: 2px solid #ddd !important;
              }
              .bring-down-digit { 
                background-color: #e8f4fd !important; 
                color: #333 !important;
                border: 2px solid #ddd !important;
              }
              .divisor-input { 
                background-color: #f8f9fa !important; 
                color: #333 !important;
              }
              .dividend-digit { 
                background-color: #f8f9fa !important; 
                color: #333 !important;
              }
              
              /* Ensure lines are visible in print */
              .vertical-line { 
                border-left: 2px solid #333 !important; 
                background-color: #333 !important;
              }
              .horizontal-line-above-dividend { 
                border-bottom: 2px solid #333 !important; 
                background-color: #333 !important;
              }
              .subtraction-line { 
                border-bottom: 2px solid #333 !important; 
                background-color: #333 !important;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  // Calculate results
  const calculateResults = () => {
    const correctCount = examQuestions.filter(q => q.isCorrect).length;
    const totalCount = examQuestions.length;
    const score = Math.round((correctCount / totalCount) * 100);
    
    return { correctCount, totalCount, score };
  };

  // Print plain questions (simple format without BellMaths UI design)
  const printPlainQuestions = () => {
    // Track print answers
    metricsTracker.trackPrintAnswers('division', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    
    let plainContent = `
      <html>
        <head>
          <title>Math Exam - Questions</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              color: #333;
            }
            .question { 
              margin-bottom: 30px; 
              page-break-inside: avoid;
            }
            .question-number {
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
            }
            .math-problem {
              font-size: 18px;
              margin: 15px 0;
              padding: 15px;
              border: 1px solid #ddd;
              background-color: #f9f9f9;
            }
            .answer-space {
              margin-top: 20px;
              padding: 10px;
              border: 1px solid #ccc;
              background-color: #fff;
              min-height: 40px;
            }
            @media print {
              body { margin: 15px; }
              .question { margin-bottom: 25px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Math Exam</h1>
            <p>Student Name: _________________</p>
            <p>Date: _________________</p>
            <p>Total Questions: ${examQuestions.length}</p>
        </div>
      `;
      
    examQuestions.forEach((question, index) => {
      plainContent += `
        <div class="question">
          <div class="question-number">Question ${index + 1}</div>
          <div class="math-problem">
            ${question.dividend} ÷ ${question.divisor} = ?
          </div>
          <div class="answer-space">
            Answer: _________________
          </div>
        </div>
      `;
    });

    plainContent += `
        </body>
      </html>
    `;
    
    printWindow.document.write(plainContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Pad number for display
  const padNumber = (num, length) => {
    if (!num) return ' '.repeat(length);
    return num.toString().padStart(length, ' ');
  };

  // Handle input focus for virtual keyboard
  const handleInputFocus = (type, stepIndex = null, digitIndex = null) => {
    // Find which question this input belongs to by searching through all questions
    // We need to find the question that contains the currently focused element
    let questionId = null;
    let questionElement = null;
    
    // Get the currently focused element
    const activeElement = document.activeElement;
    
    // Search through all questions to find which one contains this element
    for (let qId in mathProblemRefs.current) {
      if (mathProblemRefs.current[qId]?.contains(activeElement)) {
        questionId = parseInt(qId);
        questionElement = mathProblemRefs.current[qId];
        break;
      }
    }
    
    console.log('handleInputFocus debug:', { type, stepIndex, digitIndex, questionId, activeElement, questionElement });
    
    if (activeElement && questionElement) {
      const rect = activeElement.getBoundingClientRect();
      const mathProblemRect = questionElement.getBoundingClientRect();
      
      console.log('Positioning calculation:', { rect, mathProblemRect });
      
      if (mathProblemRect) {
        // Position keyboard relative to the specific question's math problem container
        // This ensures each question gets its own keyboard positioning
        let x = rect.right - mathProblemRect.left + 5;
        let y = rect.top - mathProblemRect.top + 40;
        
        // Ensure keyboard doesn't go off-screen horizontally
        if (x + 300 > window.innerWidth) { // 300px is approximate keyboard width
          x = rect.left - mathProblemRect.left - 300; // Position to the left of input
        }
        
        console.log('Setting keyboard position:', { x, y });
        setKeyboardPosition({ x, y });
        setActiveInput({ questionId, type, stepIndex, digitIndex });
    setKeyboardVisible(true);
      }
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Keep keyboard visible for now, let click outside handle hiding
  };

  // Handle virtual keyboard key press
  const handleVirtualKeyPress = (key) => {
    if (!activeInput) return;
    
    const { questionId, type, stepIndex, digitIndex } = activeInput;
    let inputElement;
    
    // Find the input element within the specific question's math problem container
    const questionContainer = mathProblemRefs.current[questionId];
    if (!questionContainer) {
      console.log('Question container not found for questionId:', questionId);
      return;
    }
    
    if (type === 'quotient-digit') {
      inputElement = questionContainer.querySelector(`[data-index="quotient-digit-${stepIndex}"]`);
      if (inputElement) {
      handleQuotientDigitChange(questionId, stepIndex, key);
        inputElement.focus();
      }
    } else if (type === 'partial-product-digit') {
      inputElement = questionContainer.querySelector(`[data-index="partial-product-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
      handlePartialProductDigitChange(questionId, stepIndex, digitIndex, key);
        inputElement.focus();
      }
    } else if (type === 'remainder-digit') {
      inputElement = questionContainer.querySelector(`[data-index="remainder-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
      handleRemainderDigitChange(questionId, stepIndex, digitIndex, key);
        inputElement.focus();
      }
    } else if (type === 'bring-down-digit') {
      inputElement = questionContainer.querySelector(`[data-index="bring-down-digit-${stepIndex}"]`);
      if (inputElement) {
      handleBringDownDigitChange(questionId, stepIndex, key);
        inputElement.focus();
    }
    }
    
    // Keep focus on current input (manual navigation for exam)
    // Don't automatically move to next input
  };

  // Handle virtual keyboard backspace
  const handleVirtualBackspace = () => {
    if (!activeInput) return;
    
    const { questionId, type, stepIndex, digitIndex } = activeInput;
    let inputElement;
    
    // Find the input element within the specific question's math problem container
    const questionContainer = mathProblemRefs.current[questionId];
    if (!questionContainer) {
      console.log('Question container not found for questionId:', questionId);
      return;
    }
    
    if (type === 'quotient-digit') {
      inputElement = questionContainer.querySelector(`[data-index="quotient-digit-${stepIndex}"]`);
      if (inputElement) {
      const currentValue = examQuestions.find(q => q.id === questionId)?.userQuotientDigits[stepIndex] || '';
      handleQuotientDigitChange(questionId, stepIndex, currentValue.slice(0, -1));
        inputElement.focus();
      }
    } else if (type === 'partial-product-digit') {
      inputElement = questionContainer.querySelector(`[data-index="partial-product-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
      const currentValue = examQuestions.find(q => q.id === questionId)?.userPartialProducts[stepIndex]?.[digitIndex] || '';
      handlePartialProductDigitChange(questionId, stepIndex, digitIndex, currentValue.slice(0, -1));
        inputElement.focus();
      }
    } else if (type === 'remainder-digit') {
      inputElement = questionContainer.querySelector(`[data-index="remainder-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
      const currentValue = examQuestions.find(q => q.id === questionId)?.userRemainders[stepIndex]?.[digitIndex] || '';
      handleRemainderDigitChange(questionId, stepIndex, digitIndex, currentValue.slice(0, -1));
        inputElement.focus();
      }
    } else if (type === 'bring-down-digit') {
      inputElement = questionContainer.querySelector(`[data-index="bring-down-digit-${stepIndex}"]`);
      if (inputElement) {
      const currentValue = examQuestions.find(q => q.id === questionId)?.userBringDowns[stepIndex] || '';
      handleBringDownDigitChange(questionId, stepIndex, currentValue.slice(0, -1));
        inputElement.focus();
    }
    }
    
    // Keep focus on current input (manual navigation for exam)
  };

  // Handle virtual keyboard clear
  const handleVirtualClear = () => {
    if (!activeInput) return;
    
    const { questionId, type, stepIndex, digitIndex } = activeInput;
    let inputElement;
    
    // Find the input element within the specific question's math problem container
    const questionContainer = mathProblemRefs.current[questionId];
    if (!questionContainer) {
      console.log('Question container not found for questionId:', questionId);
      return;
    }
    
    if (type === 'quotient-digit') {
      inputElement = questionContainer.querySelector(`[data-index="quotient-digit-${stepIndex}"]`);
      if (inputElement) {
      handleQuotientDigitChange(questionId, stepIndex, '');
        inputElement.focus();
      }
    } else if (type === 'partial-product-digit') {
      inputElement = questionContainer.querySelector(`[data-index="partial-product-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
      handlePartialProductDigitChange(questionId, stepIndex, digitIndex, '');
        inputElement.focus();
      }
    } else if (type === 'remainder-digit') {
      inputElement = questionContainer.querySelector(`[data-index="remainder-digit-${stepIndex}-${digitIndex}"]`);
      if (inputElement) {
      handleRemainderDigitChange(questionId, stepIndex, digitIndex, '');
        inputElement.focus();
      }
    } else if (type === 'bring-down-digit') {
      inputElement = questionContainer.querySelector(`[data-index="bring-down-digit-${stepIndex}"]`);
      if (inputElement) {
      handleBringDownDigitChange(questionId, stepIndex, '');
        inputElement.focus();
    }
    }
    
    // Keep focus on current input (manual navigation for exam)
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, questionId, type, stepIndex = null, digitIndex = null) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextInput = getNextInput(questionId, type, stepIndex, digitIndex);
      if (nextInput) {
        focusInput(nextInput);
      }
    }
  };

  // Get next input to focus
  const getNextInput = (questionId, type, stepIndex, digitIndex) => {
    const question = examQuestions.find(q => q.id === questionId);
    if (!question) return null;
    
    const steps = question.divisionSteps;
    
    if (type === 'quotient-digit') {
      // Move to partial product of same step
      return { questionId, type: 'partial-product-digit', stepIndex, digitIndex: 0 };
    } else if (type === 'partial-product-digit') {
      if (digitIndex < question.divisor.length - 1) {
        // Move to next partial product digit
        return { questionId, type: 'partial-product-digit', stepIndex, digitIndex: digitIndex + 1 };
      } else {
        // Move to remainder of same step
        return { questionId, type: 'remainder-digit', stepIndex, digitIndex: 0 };
      }
    } else if (type === 'remainder-digit') {
      if (digitIndex < question.remainder.length - 1) {
        // Move to next remainder digit
        return { questionId, type: 'remainder-digit', stepIndex, digitIndex: digitIndex + 1 };
      } else if (stepIndex < steps.length - 1 && steps[stepIndex].bringDown) {
        // Move to bring down of same step
        return { questionId, type: 'bring-down-digit', stepIndex };
      } else if (stepIndex < steps.length - 1) {
        // Move to quotient of next step
        return { questionId, type: 'quotient-digit', stepIndex: stepIndex + 1 };
      } else {
        // Move to next question
        const nextQuestion = examQuestions.find(q => q.id > questionId);
        if (nextQuestion) {
          return { questionId: nextQuestion.id, type: 'quotient-digit', stepIndex: 0 };
        }
      }
    } else if (type === 'bring-down-digit') {
      if (stepIndex < steps.length - 1) {
        // Move to quotient of next step
        return { questionId, type: 'quotient-digit', stepIndex: stepIndex + 1 };
      } else {
        // Move to next question
        const nextQuestion = examQuestions.find(q => q.id > questionId);
        if (nextQuestion) {
          return { questionId: nextQuestion.id, type: 'quotient-digit', stepIndex: 0 };
        }
      }
    }
    
    return null;
  };

  // Focus specific input
  const focusInput = (inputInfo) => {
    const { questionId, type, stepIndex, digitIndex } = inputInfo;
    
    // Use the correct selector format that matches the data-index attributes
    let selector;
    if (type === 'quotient-digit') {
      selector = `[data-index="quotient-digit-${stepIndex}"]`;
    } else if (type === 'partial-product-digit') {
      selector = `[data-index="partial-product-digit-${stepIndex}-${digitIndex}"]`;
    } else if (type === 'remainder-digit') {
      selector = `[data-index="remainder-digit-${stepIndex}-${digitIndex}"]`;
    } else if (type === 'bring-down-digit') {
      selector = `[data-index="bring-down-digit-${stepIndex}"]`;
    } else {
      selector = `[data-index="${type}"]`;
    }
    
    const inputElement = document.querySelector(selector);
    if (inputElement) {
      inputElement.focus();
    }
  };



  // Handle click outside to hide keyboard
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (keyboardVisible && !event.target.closest('.virtual-keyboard') && 
          !event.target.closest('input') && 
          !event.target.closest('button')) {
        setKeyboardVisible(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [keyboardVisible]); // Add missing dependency

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(timeRemaining => {
          if (timeRemaining <= 1) {
            setTimerActive(false);
            submitExam();
            return 0;
          }
          return timeRemaining - 1;
        });
      }, 1000);
    } else if (timeRemaining === 0) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeRemaining]);

  // Exam setup page
  const renderExamSetup = () => (
    <div className="exam-setup">
      <h2>Division Exam Setup</h2>
      <div className="setup-form">
        <div className="form-group">
          <label>Number of Questions:</label>
          <select 
            value={examConfig.questionCount} 
            onChange={(e) => setExamConfig({...examConfig, questionCount: parseInt(e.target.value)})}
          >
            {[1, 2, 5, 10, 15, 20].map(count => (
              <option 
                key={count} 
                value={count}
                disabled={count > 2}
                style={count > 2 ? { color: '#999', fontStyle: 'italic' } : {}}
              >
                {count} {count === 1 ? 'Question' : 'Questions'}
                {count > 2 && ' (Upgrade to Pro Edition)'}
              </option>
            ))}
          </select>
          {examConfig.questionCount > 2 && (
            <div style={{ 
              marginTop: '8px', 
              padding: '8px 12px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7', 
              borderRadius: '4px',
              fontSize: '14px',
              color: '#856404'
            }}>
              ⚠️ Upgrade to Pro Edition to support {examConfig.questionCount} questions
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label>Level:</label>
          <select 
            value={examConfig.gradeLevel} 
            onChange={(e) => setExamConfig({...examConfig, gradeLevel: e.target.value})}
          >
            {Array.from({ length: 8 }, (_, i) => i + 1).map(level => (
              <option 
                key={level} 
                value={level.toString()}
                disabled={level > 3}
                style={{ color: level > 3 ? '#999' : 'inherit' }}
              >
                {level > 3 ? `Level ${level} (Upto ${level + 1} digits) - Upgrade to Pro Edition` : `Level ${level} (Upto ${level + 1} digits)`}
              </option>
            ))}
          </select>
        </div>

        {/* Color Coding Explanation */}
        <div className="color-coding-info" style={{
          backgroundColor: '#f8f9fa',
          padding: '15px',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          marginBottom: '20px'
        }}>
          <h4 style={{ color: '#667eea', marginBottom: '10px', marginTop: '0' }}>Color Coding Guide</h4>
          <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>Red:</span> Quotient digits (your answers)
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: '#27ae60', fontWeight: 'bold' }}>Green:</span> Partial products (quotient × divisor)
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: '#f39c12', fontWeight: 'bold' }}>Orange:</span> Remainders
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: '#9b59b6', fontWeight: 'bold' }}>Purple:</span> Bring down digits
            </div>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: '#667eea', fontWeight: 'bold' }}>Blue:</span> Headers and labels
            </div>
          </div>
        </div>
        
        <div className="form-group">
          <label>
            <input 
              type="checkbox" 
              checked={examConfig.useTimer} 
              onChange={(e) => setExamConfig({...examConfig, useTimer: e.target.checked})}
            />
            Use Timer
          </label>
        </div>
        
        {examConfig.useTimer && (
          <div className="form-group">
            <label>Time Limit (minutes):</label>
            <input 
              type="number" 
              min="1" 
              max="120" 
              value={examConfig.timeLimit} 
              onChange={(e) => setExamConfig({...examConfig, timeLimit: parseInt(e.target.value)})}
            />
          </div>
        )}
        
        <div className="setup-buttons">
          <button className="btn btn-primary" onClick={generateExam}>
            Start Exam
          </button>
        </div>
      </div>
    </div>
  );

  // Render division problem
    const renderDivisionProblem = (question, questionNumber) => {
    const { dividend, divisor, divisionSteps } = question;
    const dividendDigits = dividend.split('').map((digit, index) => ({ value: digit, index }));
    
    return (
      <div 
        key={question.id}
        ref={(el) => (mathProblemRefs.current[question.id] = el)}
        className="math-problem"
        style={{
          position: 'relative',
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          marginBottom: '30px',
          border: '2px solid #e9ecef'
        }}
      >
        {/* Question Header - Integrated into the main container */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0', color: '#667eea', fontSize: '18px' }}>
            Question {questionNumber}
          </h3>
          {/* Question Status */}
          <div>
            {question.isCorrect === true && <span className="correct" style={{ color: '#27ae60', fontWeight: 'bold' }}>✓ Correct</span>}
            {question.isCorrect === false && <span className="incorrect" style={{ color: '#e74c3c', fontWeight: 'bold' }}>✗ Incorrect</span>}
          </div>
        </div>

        {/* Traditional Long Division Layout - Exactly 4 layouts like practice module */}
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '0px'
        }}>
          {/* Layout 1: Quotient Row - Above horizontal line */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            marginBottom: '5px',
            marginLeft: `${calculateQuotientMargin(divisionSteps, divisor)}px`
          }}>
              {divisionSteps.map((step, stepIndex) => (
                <input
                key={stepIndex}
                  type="text"
                  value={question.userQuotientDigits[stepIndex] || ''}
                  onChange={(e) => handleQuotientDigitChange(question.id, stepIndex, e.target.value)}
                                        onFocus={() => handleInputFocus('quotient-digit', stepIndex)}
                  onBlur={handleInputBlur}
                  onKeyDown={(e) => handleKeyDown(e, question.id, 'quotient-digit', stepIndex)}
                data-question={question.id}
                data-index={`quotient-digit-${stepIndex}`}
                  style={{
                    width: '40px',
                    height: '40px',
                    textAlign: 'center',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  color: '#e74c3c',
                  marginRight: '2px',
                  outline: 'none',
                  backgroundColor: '#ffeaea'
                  }}
                  maxLength={1}
                  placeholder=""
                />
              ))}
          </div>
          
          {/* Layout 2: Main Division Structure (Divisor + Vertical Line + Dividend) */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: '10px'
          }}>
            {/* Divisor */}
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: '10px'
            }}>
              <input
                type="text"
                value={divisor}
                readOnly
                style={{
                  width: `${Math.max(40, divisor.toString().length * 20)}px`,
                  height: '40px',
                  textAlign: 'center',
                  border: 'none',
                  borderRadius: '0',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: '5px',
                  backgroundColor: '#f8f9fa',
                  outline: 'none'
                }}
                maxLength={3}
                placeholder="?"
              />
            </div>

            {/* Vertical Line */}
            <div style={{
              width: '2px',
              height: `${calculateVerticalLineHeight(divisionSteps)}px`,
              backgroundColor: '#333',
              marginTop: '10px'
            }}></div>



            {/* Dividend and Working Area */}
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}>
              {/* Horizontal Line Above Dividend */}
              <div style={{
                ...calculateHorizontalLineWidth(0, dividendDigits.length, divisionSteps),
                height: '2px',
                backgroundColor: '#333',
                marginBottom: '10px'
              }}></div>

              {/* Dividend - Individual digit fields */}
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                marginBottom: '15px',
                gap: '2px'
              }}>
                {dividendDigits.map((digit, index) => (
              <input
                    key={index}
                type="text"
                    value={digit.value}
                readOnly
                style={{
                      width: '40px',
                  height: '40px',
                  textAlign: 'center',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#333',
                  backgroundColor: '#f8f9fa',
                  outline: 'none'
                }}
                    maxLength={1}
                    placeholder={digit.value}
              />
                ))}
          </div>
          
              {/* Layout 3: Division Steps */}
          {divisionSteps.map((step, stepIndex) => (
                <div key={stepIndex} style={{ marginBottom: '10px' }}>
                  {/* Partial Product */}
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '5px',
                    marginLeft: `${step.partialProductStartPosition * 42}px`
                  }}>
                    {step.partialProduct.toString().split('').map((digit, digitIndex) => (
                  <input
                        key={digitIndex}
                    type="text"
                        value={question.userPartialProducts?.[stepIndex]?.[digitIndex] || ''}
                    onChange={(e) => handlePartialProductDigitChange(question.id, stepIndex, digitIndex, e.target.value)}
                        onFocus={() => handleInputFocus('partial-product-digit', stepIndex, digitIndex)}
                    onBlur={handleInputBlur}
                    onKeyDown={(e) => handleKeyDown(e, question.id, 'partial-product-digit', stepIndex, digitIndex)}
                        data-question={question.id}
                        data-index={`partial-product-digit-${stepIndex}-${digitIndex}`}
                    style={{
                      width: '40px',
                      height: '40px',
                      textAlign: 'center',
                      border: 'none',
                      borderRadius: '4px',
                          backgroundColor: '#e8f5e8',
                      fontSize: '16px',
                      fontWeight: 'bold',
                          color: '#27ae60',
                          marginRight: '2px',
                      outline: 'none'
                    }}
                    maxLength={1}
                    placeholder=""
                  />
                ))}
              </div>
              
                  {/* Subtraction Line */}
                  <div style={{ 
                    borderBottom: '2px solid #333',
                    ...calculateHorizontalLineWidth(0, step.partialProduct.toString().length, divisionSteps, stepIndex),
                height: '2px',
                marginBottom: '10px'
              }}></div>
              
                  {/* Remainder and Bring Down on same row */}
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '5px'
                  }}>
                    {/* Remainder */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      marginLeft: `${step.remainderStartPosition * 42}px`
                    }}>
                      {step.remainder.toString().split('').map((digit, digitIndex) => (
                  <input
                          key={digitIndex}
                    type="text"
                          value={question.userRemainders?.[stepIndex]?.[digitIndex] || ''}
                    onChange={(e) => handleRemainderDigitChange(question.id, stepIndex, digitIndex, e.target.value)}
                          onFocus={() => handleInputFocus('remainder-digit', stepIndex, digitIndex)}
                    onBlur={handleInputBlur}
                    onKeyDown={(e) => handleKeyDown(e, question.id, 'remainder-digit', stepIndex, digitIndex)}
                          data-question={question.id}
                          data-index={`remainder-digit-${stepIndex}-${digitIndex}`}
                    style={{
                      width: '40px',
                      height: '40px',
                      textAlign: 'center',
                      border: 'none',
                      borderRadius: '4px',
                            backgroundColor: '#fff3e0',
                      fontSize: '16px',
                      fontWeight: 'bold',
                            color: '#f39c12',
                            marginRight: '2px',
                      outline: 'none'
                    }}
                    maxLength={1}
                    placeholder=""
                  />
                ))}
                    </div>
                
                    {/* Bring Down on same row with arrow on right side */}
                {step.bringDown && (
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '2px'
                      }}>
                    <input
                      type="text"
                          value={question.userBringDowns?.[stepIndex] || ''}
                      onChange={(e) => handleBringDownDigitChange(question.id, stepIndex, e.target.value)}
                          onFocus={() => handleInputFocus('bring-down-digit', stepIndex)}
                      onBlur={handleInputBlur}
                      onKeyDown={(e) => handleKeyDown(e, question.id, 'bring-down-digit', stepIndex)}
                          data-question={question.id}
                          data-index={`bring-down-digit-${stepIndex}`}
                      style={{
                        width: '40px',
                        height: '40px',
                        textAlign: 'center',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        color: '#333',
                        backgroundColor: '#e8f4fd',
                        marginRight: '5px',
                        outline: 'none'
                      }}
                      maxLength={1}
                      placeholder=""
                    />
                        <span style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold',
                          color: '#333'
                        }}>
                          ↓
                        </span>
                      </div>
                )}
              </div>
            </div>
          ))}
        </div>
          </div>
        </div>

        {/* Layout 4: Virtual Keyboard */}
        {(() => {
          const shouldShow = keyboardVisible && activeInput && activeInput.questionId === question.id;
          console.log(`Virtual keyboard for question ${question.id}:`, { 
            keyboardVisible, 
            activeInput: activeInput ? { questionId: activeInput.questionId, type: activeInput.type } : null, 
            shouldShow 
          });
          return shouldShow ? (
            <VirtualKeyboard
              onKeyPress={handleVirtualKeyPress}
              onBackspace={handleVirtualBackspace}
              onClear={handleVirtualClear}
              position={keyboardPosition}
              isVisible={keyboardVisible}
            />
          ) : null;
        })()}

        {/* Step-by-Step Solution - Show after exam completion */}
        {examCompleted && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginTop: '20px',
            border: '1px solid #e9ecef'
          }}>
            <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>Step-by-Step Solution</h3>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              {(() => {
                const solutionSteps = getSolutionSteps(question);
                return (
                  <>
                    <p><strong>Problem:</strong> {solutionSteps.dividend} ÷ {solutionSteps.divisor}</p>
                    <p><strong>Answer:</strong> {solutionSteps.quotient} R {solutionSteps.remainder}</p>
                    
                    <div style={{ marginTop: '15px' }}>
                      <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>Working Steps:</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #dee2e6'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              <th style={{
                                padding: '12px',
                                textAlign: 'left',
                                borderBottom: '2px solid #dee2e6',
                                color: '#2c3e50',
                                fontWeight: 'bold'
                              }}>Step</th>
                              <th style={{
                                padding: '12px',
                                textAlign: 'left',
                                borderBottom: '2px solid #dee2e6',
                                color: '#2c3e50',
                                fontWeight: 'bold'
                              }}>Quotient Digit</th>
                              <th style={{
                                padding: '12px',
                                textAlign: 'left',
                                borderBottom: '2px solid #dee2e6',
                                color: '#2c3e50',
                                fontWeight: 'bold'
                              }}>Partial Product</th>
                              <th style={{
                                padding: '12px',
                                textAlign: 'left',
                                borderBottom: '2px solid #dee2e6',
                                color: '#2c3e50',
                                fontWeight: 'bold'
                              }}>Remainder</th>
                              <th style={{
                                padding: '12px',
                                textAlign: 'left',
                                borderBottom: '2px solid #dee2e6',
                                color: '#2c3e50',
                                fontWeight: 'bold'
                              }}>Bring Down</th>
                            </tr>
                          </thead>
                          <tbody>
                            {solutionSteps.steps.map((step, index) => (
                              <tr key={index} style={{
                                borderBottom: '1px solid #dee2e6'
                              }}>
                                <td style={{
                                  padding: '12px',
                                  fontWeight: 'bold',
                                  color: '#2c3e50'
                                }}>{index + 1}</td>
                                <td style={{
                                  padding: '12px',
                                  color: '#e74c3c',
                                  fontWeight: 'bold'
                                }}>{step.quotientDigit}</td>
                                <td style={{
                                  padding: '12px',
                                  color: '#27ae60',
                                  fontWeight: 'bold'
                                }}>
                                  {step.partialProduct} ({step.quotientDigit} × {solutionSteps.divisor})
                                </td>
                                <td style={{
                                  padding: '12px',
                                  color: '#f39c12',
                                  fontWeight: 'bold'
                                }}>
                                  {step.remainder} ({step.workingNumber} - {step.partialProduct})
                                </td>
                                <td style={{
                                  padding: '12px',
                                  color: '#333'
                                }}>
                                  {step.bringDown ? `${step.bringDown} → (${step.remainder} + ${step.bringDown})` : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render exam questions
  const renderExamQuestions = () => {
    const startIndex = (currentPage - 1) * questionsPerPage;
    const endIndex = startIndex + questionsPerPage;
    const currentQuestions = examQuestions.slice(startIndex, endIndex);

    return (
      <div className="exam-questions">
        <div className="exam-header">
          <div className="exam-info">
          <h2>Division Exam - Grade {examConfig.gradeLevel}</h2>
          {examConfig.useTimer && (
              <div style={{ 
                marginTop: '10px',
                padding: '10px 15px',
                backgroundColor: timeRemaining <= 300 ? '#f8d7da' : '#d4edda',
                borderRadius: '6px',
                border: `2px solid ${timeRemaining <= 300 ? '#dc3545' : '#28a745'}`,
                textAlign: 'center'
              }}>
                <strong style={{ 
                  color: timeRemaining <= 300 ? '#721c24' : '#155724',
                  fontSize: '18px'
                }}>
                  Time Remaining: {formatTime(timeRemaining)}
                </strong>
            </div>
          )}
          </div>
          
          <div className="exam-actions" style={{ 
            display: 'flex', 
            gap: '10px', 
            justifyContent: 'center', 
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            {!examCompleted ? (
              <>
                {/* Before exam submission - show both print buttons */}
                <button className="btn btn-secondary" onClick={printQuestionsOnly}>
                  Print BellMaths Notebook
                </button>
                <button className="btn btn-secondary" onClick={printPlainQuestions}>
                  Print Questions
                </button>
              </>
            ) : (
              <>
                {/* After exam submission - show both print buttons */}
                <button className="btn btn-secondary" onClick={printExam}>
                  Print BellMaths Notebook with Answers
                </button>
                <button className="btn btn-secondary" onClick={printPlainQuestions}>
                  Print Questions
                </button>
              </>
            )}
            {/* Restart Exam Button - After submission */}
            {showResults && (
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setExamCompleted(false);
                  setShowResults(false);
                  setExamQuestions([]);
                  setCurrentPage(1);
                  setTimeRemaining(0);
                  setTimerActive(false);
                  setExamStarted(false); // Reset to setup page
                }}
                style={{ 
                  padding: '10px 20px', 
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Restart Exam
              </button>
            )}
            {!examCompleted && (
              <button className="btn btn-success" onClick={submitExam}>
                Submit Exam
              </button>
            )}
          </div>
        </div>

        {/* Exam Results Summary - Positioned above question 1 as requested */}
        {showResults && (
          <div className="exam-results" style={{ 
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #667eea'
          }}>
            <h3 style={{ color: '#667eea', marginBottom: '15px' }}>Exam Results Summary</h3>
            <div className="results-summary" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Correct Answers:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{calculateResults().correctCount}/{calculateResults().totalCount}</span>
              </div>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Percentage:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{calculateResults().score}%</span>
            </div>
            </div>
          </div>
        )}

        {/* Question Navigation Bar */}
        {!showResults && examQuestions.length > 0 && (
          <div style={{
            position: 'sticky',
            top: '0',
            zIndex: 100,
            backgroundColor: 'white',
            padding: '15px',
            borderBottom: '2px solid #e9ecef',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <h4 style={{ margin: 0, color: '#667eea' }}>Quick Navigation</h4>
              <span style={{ fontSize: '14px', color: '#666' }}>
                Click any question number to jump to it
              </span>
                  </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              maxHeight: '120px',
              overflowY: 'auto'
            }}>
              {examQuestions.map((question, index) => {
                const hasAnswer = question.userQuotientDigits && question.userQuotientDigits.some(digit => digit);
                return (
                  <button
                    key={question.id}
                    onClick={() => {
                      const questionElement = document.getElementById(`question-${question.id}`);
                      if (questionElement) {
                        questionElement.scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center' 
                        });
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '2px solid',
                      borderColor: hasAnswer ? '#28a745' : '#e9ecef',
                      backgroundColor: hasAnswer ? '#d4edda' : 'white',
                      color: hasAnswer ? '#155724' : '#666',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '40px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    {question.id}
                  </button>
                );
              })}
              </div>
            </div>
        )}

        {currentQuestions.map((question, index) => (
          <div key={question.id} id={`question-${question.id}`}>
            {renderDivisionProblem(question, startIndex + index + 1)}
          </div>
        ))}



        {/* Pagination - Only show before exam submission */}
        {examStarted && !showResults && (
          <div className="exam-pagination" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
            marginTop: '30px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
          <button 
            className="btn btn-secondary" 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
          >
            Previous
          </button>
          
            <span className="page-info" style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#667eea'
            }}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button 
            className="btn btn-secondary" 
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
          >
            Next
          </button>
        </div>
        )}

        {/* Bottom Submit Exam Button */}
        {examStarted && !examCompleted && (
          <div style={{ 
            textAlign: 'center', 
            marginTop: '30px', 
            padding: '20px',
            borderTop: '2px solid #e9ecef'
          }}>
            <button className="btn btn-success btn-large" onClick={submitExam}>
            Submit Exam
          </button>
        </div>
        )}



      </div>
    );
  };

  // Render results page
  const renderResults = () => {
    const { correctCount, totalCount, score } = calculateResults();
    const { papersSaved, totalLines } = calculatePaperSavings();

    return (
      <div className="exam-results">
        <h2>Exam Results</h2>
        
        <div className="results-summary">
          <div className="result-card">
            <h3>Score</h3>
            <div className="score">{score}%</div>
            <p>{correctCount} out of {totalCount} questions correct</p>
          </div>
          
          <div className="result-card">
            <h3>Time Taken</h3>
            <div className="time">{formatTime(examConfig.timeLimit * 60 - timeRemaining)}</div>
            <p>Time limit: {formatTime(examConfig.timeLimit * 60)}</p>
          </div>
          
          <div className="result-card">
            <h3>Paper Saved</h3>
            <div className="paper-saved">{papersSaved}</div>
            <p>{totalLines} lines of paper saved</p>
          </div>
        </div>

        <div className="action-buttons">
          <button className="btn btn-primary" onClick={printExam}>
            Print BellMaths Notebook with Answers
          </button>
          <button className="btn btn-secondary" onClick={printQuestionsOnly}>
            Print BellMaths Notebook
          </button>
          <button className="btn btn-success" onClick={() => {
            setExamCompleted(false);
            setShowResults(false);
            setExamQuestions([]);
            setCurrentPage(1);
            setTimeRemaining(0);
            setTimerActive(false);
          }}>
            Start New Exam
          </button>
        </div>

        {/* Step-by-Step Solutions for Each Question */}
        <div style={{ marginTop: '40px' }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '20px', textAlign: 'center' }}>Step-by-Step Solutions</h3>
          
          {examQuestions.map((question, questionIndex) => {
            const solutionSteps = getSolutionSteps(question);
            return (
              <div key={question.id} style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '15px' }}>Question {questionIndex + 1}</h4>
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <p><strong>Problem:</strong> {solutionSteps.dividend} ÷ {solutionSteps.divisor}</p>
                  <p><strong>Answer:</strong> {solutionSteps.quotient} R {solutionSteps.remainder}</p>
                  
                  <div style={{ marginTop: '15px' }}>
                    <h5 style={{ color: '#2c3e50', marginBottom: '10px' }}>Working Steps:</h5>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #dee2e6'
                      }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{
                              padding: '12px',
                              textAlign: 'left',
                              borderBottom: '2px solid #dee2e6',
                              color: '#2c3e50',
                              fontWeight: 'bold'
                            }}>Step</th>
                            <th style={{
                              padding: '12px',
                              textAlign: 'left',
                              borderBottom: '2px solid #dee2e6',
                              color: '#2c3e50',
                              fontWeight: 'bold'
                            }}>Quotient Digit</th>
                            <th style={{
                              padding: '12px',
                              textAlign: 'left',
                              borderBottom: '2px solid #dee2e6',
                              color: '#2c3e50',
                              fontWeight: 'bold'
                            }}>Partial Product</th>
                            <th style={{
                              padding: '12px',
                              textAlign: 'left',
                              borderBottom: '2px solid #dee2e6',
                              color: '#2c3e50',
                              fontWeight: 'bold'
                            }}>Remainder</th>
                            <th style={{
                              padding: '12px',
                              textAlign: 'left',
                              borderBottom: '2px solid #dee2e6',
                              color: '#2c3e50',
                              fontWeight: 'bold'
                            }}>Bring Down</th>
                          </tr>
                        </thead>
                        <tbody>
                          {solutionSteps.steps.map((step, index) => (
                            <tr key={index} style={{
                              borderBottom: '1px solid #dee2e6'
                            }}>
                              <td style={{
                                padding: '12px',
                                fontWeight: 'bold',
                                color: '#2c3e50'
                              }}>{index + 1}</td>
                              <td style={{
                                padding: '12px',
                                color: '#e74c3c',
                                fontWeight: 'bold'
                              }}>{step.quotientDigit}</td>
                              <td style={{
                                padding: '12px',
                                color: '#27ae60',
                                fontWeight: 'bold'
                              }}>
                                {step.partialProduct} ({step.quotientDigit} × {solutionSteps.divisor})
                              </td>
                              <td style={{
                                padding: '12px',
                                color: '#f39c12',
                                fontWeight: 'bold'
                              }}>
                                {step.remainder} ({step.workingNumber} - {step.partialProduct})
                              </td>
                              <td style={{
                                padding: '12px',
                                color: '#333'
                              }}>
                                {step.bringDown ? `${step.bringDown} → (${step.remainder} + ${step.bringDown})` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Main render logic
  if (!examStarted) {
    return renderExamSetup();
  }

  return renderExamQuestions();
};

export default DivisionExam; 